import { useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'

interface DetalleData {
	detalleSilobolsas: string
	coberturaInundacion: boolean
	coberturaRobo: boolean
}

interface SilobolsaDetalleScreenProps {
	onNext: (data: DetalleData) => void
	onBack: () => void
	data?: Partial<DetalleData>
}

export function SilobolsaDetalleScreen({
	onNext,
	onBack,
	data,
}: SilobolsaDetalleScreenProps) {
	const [formData, setFormData] = useState<DetalleData>({
		detalleSilobolsas: data?.detalleSilobolsas || '',
		coberturaInundacion: data?.coberturaInundacion || false,
		coberturaRobo: data?.coberturaRobo || false,
	})

	const [errors, setErrors] = useState<
		Partial<Record<keyof DetalleData, string>>
	>({})

	const validateForm = (): boolean => {
		const newErrors: Partial<Record<keyof DetalleData, string>> = {}

		if (!formData.detalleSilobolsas.trim()) {
			newErrors.detalleSilobolsas = 'El detalle de silobolsas es requerido'
		}

		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleSubmit = () => {
		if (validateForm()) {
			onNext(formData)
		}
	}

	return (
		<div className="flex h-full w-full flex-col">
			<div className="flex-1 overflow-y-auto">
				<div className="flex w-full flex-col items-center gap-6">
					<h2 className="font-parkinsans text-center text-2xl leading-8 font-medium text-blue-800">
						Detalle de Silobolsas
					</h2>
					<p className="text-center text-sm text-neutral-600">
						Información particular de cada silobolsa a asegurar
					</p>

					<div className="flex w-full flex-col gap-4">
						<div className="rounded-xl bg-blue-50 p-4">
							<p className="font-inter mb-2 text-sm font-medium text-blue-800">
								Por cada silobolsa, incluir:
							</p>
							<ul className="font-inter flex flex-col gap-1 text-sm text-blue-700">
								<li>• Numeración o identificación</li>
								<li>• Tipo de grano</li>
								<li>• Tonelaje almacenado</li>
								<li>• Humedad del ensilado</li>
								<li>• Suma asegurada</li>
							</ul>
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Detalle de Silobolsas a Asegurar
							</label>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-base"
								rows={8}
								placeholder={`Ejemplo:
Silobolsa 1: Soja, 200 tn, 14% humedad, $10.000.000
Silobolsa 2: Maíz, 180 tn, 15% humedad, $8.000.000
Silobolsa 3: Trigo, 150 tn, 13% humedad, $7.500.000`}
								value={formData.detalleSilobolsas}
								onChange={(e) =>
									setFormData({
										...formData,
										detalleSilobolsas: e.target.value,
									})
								}
							/>
							{errors.detalleSilobolsas && (
								<p className="text-sm text-red-500">
									{errors.detalleSilobolsas}
								</p>
							)}
						</div>

						<div className="mt-4 rounded-xl bg-neutral-50 p-4">
							<h3 className="font-parkinsans mb-3 text-base font-medium text-blue-800">
								Coberturas Adicionales
							</h3>
							<div className="flex flex-col gap-3">
								<label className="flex cursor-pointer items-center gap-3 rounded-lg border border-neutral-200 bg-white p-3 hover:bg-neutral-50">
									<input
										type="checkbox"
										checked={formData.coberturaInundacion}
										onChange={(e) =>
											setFormData({
												...formData,
												coberturaInundacion: e.target.checked,
											})
										}
										className="size-5 rounded border-neutral-300"
									/>
									<div>
										<p className="text-sm font-medium text-neutral-800">
											Cobertura de Inundación
										</p>
										<p className="text-xs text-neutral-500">
											Protección contra daños por inundación
										</p>
									</div>
								</label>

								<label className="flex cursor-pointer items-center gap-3 rounded-lg border border-neutral-200 bg-white p-3 hover:bg-neutral-50">
									<input
										type="checkbox"
										checked={formData.coberturaRobo}
										onChange={(e) =>
											setFormData({
												...formData,
												coberturaRobo: e.target.checked,
											})
										}
										className="size-5 rounded border-neutral-300"
									/>
									<div>
										<p className="text-sm font-medium text-neutral-800">
											Cobertura de Robo
										</p>
										<p className="text-xs text-neutral-500">
											Protección contra robo del contenido
										</p>
									</div>
								</label>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="flex w-full shrink-0 flex-col items-center justify-start gap-2 bg-white pt-4 md:flex-row md:justify-between md:bg-transparent md:pt-8">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="order-2 w-full gap-2 rounded-xl px-4 py-3 text-sm leading-4 md:order-1 md:w-auto md:px-6 md:py-4 md:text-base md:leading-5"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="secondary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					className="order-1 w-full md:order-2 md:w-auto"
				>
					Enviar Cotización
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>
		</div>
	)
}
